import { useRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
import queryClient from '@/lib/queryClient'
import { indexTableQueryOptions } from '@/lib/services/index-table-query-options'
import {
  buildAdminQueryRoot,
  buildScopedAdminQueryKey,
  type AdminQueryKey,
  type AdminQueryRoot,
  type OrganizationIdInput,
} from '@/lib/organization-scoping'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  getNotificationEvents,
  syncNotificationEventRecipients,
  type NotificationEventIndexQueryKeyEntries,
} from './notification-events.api'
import type {
  NotificationEventAudience,
  SyncNotificationEventRecipientsInput,
} from './notification-events.types'

interface OrganizationScope {
  organizationId?: OrganizationIdInput
}

interface NotificationEventIndexScope extends OrganizationScope {
  audience?: NotificationEventAudience | undefined
}

/** The key entries the index fetcher reads; the scoped key builder below sets them. */
type NotificationEventIndexQueryKey = [AdminQueryRoot & NotificationEventIndexQueryKeyEntries]

/** Query options a caller may override; the key and fetcher stay owned by this module. */
type QueryParams<TOptions> = Partial<Omit<TOptions, 'queryKey' | 'queryFn'>>

export const notificationEventQueryKeys = {
  all: ({ organizationId }: OrganizationScope = {}): AdminQueryKey => [
    buildAdminQueryRoot({ entity: 'notificationEvent' }, { organizationId }),
  ],
  index: ({
    organizationId,
    audience,
  }: NotificationEventIndexScope = {}): NotificationEventIndexQueryKey =>
    buildScopedAdminQueryKey(notificationEventQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'index',
      audience,
    }),
}

export const notificationEventQueries = {
  index: ({ organizationId, audience }: NotificationEventIndexScope = {}) =>
    indexTableQueryOptions({
      queryKey: notificationEventQueryKeys.index({ organizationId, audience }),
      queryFn: getNotificationEvents,
    }),
}

export function useNotificationEvents({
  organizationId,
  audience,
  ...params
}: NotificationEventIndexScope &
  QueryParams<ReturnType<typeof notificationEventQueries.index>> = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useQuery({
    ...notificationEventQueries.index({
      organizationId: resolvedOrganizationId,
      audience,
    }),
    ...params,
  })
}

export function useSyncNotificationEventRecipientsMutation({
  organizationId,
  audience,
}: NotificationEventIndexScope = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)
  const indexQueryKey = notificationEventQueries.index({
    organizationId: resolvedOrganizationId,
    audience,
  }).queryKey

  return useMutation({
    mutationFn: (values: SyncNotificationEventRecipientsInput) =>
      syncNotificationEventRecipients({ organizationId: resolvedOrganizationId, ...values }),
    onMutate: async ({ eventId, recipientIds }) => {
      await queryClient.cancelQueries({
        queryKey: indexQueryKey,
      })

      const previousEvents = queryClient.getQueryData(indexQueryKey)
      const previousEvent = previousEvents?.find((event) => event.id === eventId) ?? null

      queryClient.setQueryData(indexQueryKey, (events = []) =>
        events.map((event) =>
          event.id === eventId
            ? {
                ...event,
                recipient_ids: recipientIds,
              }
            : event
        )
      )

      return { eventId, previousEvent }
    },
    onError: (_error, _variables, context) => {
      if (context?.previousEvent) {
        const { previousEvent } = context

        queryClient.setQueryData(indexQueryKey, (events = []) =>
          events.map((event) => (event.id === context.eventId ? previousEvent : event))
        )
      }
    },
    onSuccess: (updatedNotificationEvent) => {
      queryClient.setQueryData(indexQueryKey, (events = []) =>
        events.map((event) =>
          event.id === updatedNotificationEvent.id ? updatedNotificationEvent : event
        )
      )
    },
  })
}
